import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { DataRow, IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Copy01Icon,
  ViewIcon,
  ViewOffSlashIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { useCopyToClipboard } from "components/common/useCopyToClipboard";

/**
 * The account details panel's rows: label, value, and the reveal and copy
 * affordances the masked ones carry.
 *
 * The wire dialog does NOT use these. It shows its values unmasked behind a
 * role check and gives each row a Copy button rather than a reveal, so it has
 * its own row in AccountDetailsDialog. Two shapes, deliberately, because the
 * panel sits on screen all day and the dialog exists to hand numbers over.
 */

// Escaped rather than literal because DS_MIGRATION.md bans the character in
// source.
export const EMPTY = "\u2014";

/**
 * The guard matters: `details` comes back empty for anything that is not a
 * financial account, and repeat() on a negative count throws.
 */
export const maskDigits = (value: string): string => {
  const hidden = Math.max(value.length - 4, 0);
  // Keeping the last four cannot mask a value that IS four, so a short one is
  // covered whole rather than returned intact: it reaches here as a real
  // credential in front of a reader who is not allowed to see it.
  if (!hidden) return "•".repeat(value.length);
  // The gap between the bullets and the tail is how the design draws it.
  return `${"•".repeat(hidden)} ${value.slice(-4)}`;
};

const CopyButton: React.FC<{
  value: string;
  label: string;
  copiedMessage: string;
}> = ({ value, label, copiedMessage }) => {
  const copy = useCopyToClipboard(copiedMessage);

  return (
    <IconButton
      size="small"
      variant="ghost"
      aria-label={label}
      onClick={() => copy(value)}
    >
      <HugeiconsIcon icon={Copy01Icon} size={16} />
    </IconButton>
  );
};

interface DetailRowProps {
  label: string;
  value?: string;
  copyLabel: string;
  copiedMessage: string;
  /** Copy is an admin affordance rather than a decoration. */
  canCopy: boolean;
}

/** A row whose value is shown in full, with a copy button. */
export const CopyableDetailRow: React.FC<DetailRowProps> = ({
  label,
  value,
  copyLabel,
  copiedMessage,
  canCopy,
}) => {
  if (!value) {
    return <DataRow label={label} primaryTrailing={EMPTY} />;
  }

  return (
    <DataRow
      label={label}
      primaryTrailing={
        <Inline gap="var(--space-2)" wrap={false}>
          <Typography variant="body2">{value}</Typography>
          {canCopy && (
            <CopyButton
              value={value}
              label={copyLabel}
              copiedMessage={copiedMessage}
            />
          )}
        </Inline>
      }
    />
  );
};

interface MaskedDetailRowProps extends DetailRowProps {
  /** Labels for the reveal toggle, which read the field name aloud. */
  showLabel: string;
  hideLabel: string;
}

/** A row whose value stays masked until asked for. */
export const MaskedDetailRow: React.FC<MaskedDetailRowProps> = ({
  label,
  value,
  showLabel,
  hideLabel,
  copyLabel,
  copiedMessage,
  canCopy,
}) => {
  // WHICH value was revealed, not whether one was. The picker keeps these rows
  // mounted, and resetting a boolean in an effect still paints the new
  // account's number once before the effect runs. Comparing here means a value
  // the reader never asked for is masked in the render that delivers it.
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const revealed = canCopy && revealedValue === value;

  // Masking on the way out is not enough: holding the value would re-reveal it
  // the moment the role came back, without anyone asking again.
  if (!canCopy && revealedValue !== null) setRevealedValue(null);

  if (!value) {
    return <DataRow label={label} primaryTrailing={EMPTY} />;
  }

  return (
    <DataRow
      label={label}
      primaryTrailing={
        <Inline gap="var(--space-2)" wrap={false}>
          <Typography variant="body2">
            {revealed ? value : maskDigits(value)}
          </Typography>
          {canCopy && (
            <>
              <IconButton
                size="small"
                variant="ghost"
                aria-label={revealed ? hideLabel : showLabel}
                onClick={() =>
                  setRevealedValue(revealed ? null : (value ?? null))
                }
              >
                <HugeiconsIcon
                  icon={revealed ? ViewOffSlashIcon : ViewIcon}
                  size={16}
                />
              </IconButton>
              <CopyButton
                value={value}
                label={copyLabel}
                copiedMessage={copiedMessage}
              />
            </>
          )}
        </Inline>
      }
    />
  );
};
